import React from "react";

/**
 * Minimal markdown renderer:
 * - `## Heading` → <h2>
 * - `**bold**` → <strong>
 * - `- item` → bulleted list
 * - blank lines → paragraph breaks
 * Designed for our service/blog/city long-form content.
 */
export function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let currentList = null;
  let currentPara = [];

  const flushPara = () => {
    if (currentPara.length) {
      elements.push(
        <p key={`p-${elements.length}`} className="mb-4 leading-relaxed">
          {renderInline(currentPara.join(" "))}
        </p>
      );
      currentPara = [];
    }
  };
  const flushList = () => {
    if (currentList?.length) {
      elements.push(
        <ul key={`ul-${elements.length}`} className="list-disc pl-6 my-4 space-y-2">
          {currentList.map((item, i) => (
            <li key={i}>{renderInline(item)}</li>
          ))}
        </ul>
      );
      currentList = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushPara();
      flushList();
      continue;
    }
    if (line.startsWith("## ")) {
      flushPara();
      flushList();
      elements.push(
        <h2
          key={`h-${elements.length}`}
          className="text-2xl sm:text-3xl font-bold mt-10 mb-4 text-brand-navy"
        >
          {renderInline(line.slice(3))}
        </h2>
      );
      continue;
    }
    if (line.startsWith("- ")) {
      flushPara();
      if (!currentList) currentList = [];
      currentList.push(line.slice(2));
      continue;
    }
    flushList();
    currentPara.push(line);
  }
  flushPara();
  flushList();
  return elements;
}

function renderInline(text) {
  // Split on **bold** markers
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={i} className="font-bold text-brand-navy">
        {p.slice(2, -2)}
      </strong>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    )
  );
}
